// Stage Mail-V2.5 (2026-08-19): useSyncJob hook — poll a sync job every 3s.
//
// Used by MailV2SyncOverlay (initial / backfill / repair display) and
// by the refresh button on MailV2List (incremental spinner).
//
// Returns { job, error, isActive } where isActive is true while status is
// pending / running. Caller decides what to render on completed / failed.

import { useEffect, useRef, useState } from "react";
import { syncJobs, type MailSyncJobDTO } from "../../../api/mail-v2";

const POLL_INTERVAL_MS = 4000;

export type UseSyncJobResult = {
  job: MailSyncJobDTO | null;
  error: string | null;
  isActive: boolean;
};

export function useSyncJob(jobId: string | null): UseSyncJobResult {
  const [job, setJob] = useState<MailSyncJobDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const tick = async () => {
      try {
        const j = await syncJobs.get(jobId);
        if (cancelled) return;
        setJob(j);
        setError(null);
        if (j.status === "completed" || j.status === "failed") {
          // stop polling on terminal status
          return;
        }
        timerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        // Retry on error
        timerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobId]);

  const isActive =
    job != null &&
    (job.status === "pending" || job.status === "running" || job.status === "stale");

  return { job, error, isActive };
}
